import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { useIntroducingBrokerById } from '../hooks/useIntroducingBrokerById'
import { Loyalty } from './Loyalty'

export const LoyaltyPage: FC = () => {
  useScrollToTop()
  const { addSnackbar } = useSnackbar()
  const { callGAEvents } = useGAEvents()
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data: introducingBroker, isLoading, forceRefresh } = useIntroducingBrokerById()

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.loyaltyRewardsHistory(introducingBroker?.id, locale),
    queryFn: (pageQuery) => {
      if (introducingBroker?.id) {
        return apiClient.getIBLoyaltyRewardsHistory(introducingBroker.id, {
          ...pageQuery,
          caller: 'ca',
          languageId: locale,
        })
      }
      return Promise.resolve({
        items: [],
        itemsCount: 0,
        pageIndex: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
      })
    },
    enabled: !!introducingBroker?.id,
  })

  const onSubmitReward = async ({
    rewardId,
    rewardTierLabel,
  }: {
    rewardId: string
    rewardTierLabel?: string | null
  }) => {
    await apiClient.consumeLoyaltyReward(rewardId)

    callGAEvents().IBDashboard.LoyaltyProgram.claimRewards()
    addSnackbar.success({
      message: rewardTierLabel
        ? t('IB.Your reward for the {{levelTitle}} was successfully redeemed', {
            levelTitle: rewardTierLabel,
          })
        : t('IB.Your reward was successfully redeemed'),
    })

    forceRefresh()
    query.forceRefresh()
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading}>
      <Loyalty
        loyaltyTiers={introducingBroker?.loyaltyDetails}
        introducingBroker={introducingBroker}
        rewardsHistoryQuery={query}
        onSubmitReward={onSubmitReward}
      />
    </Loading>
  )
}
